import { FC, useEffect, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import {
  useAnimations,
  OrbitControls,
  GizmoHelper,
  GizmoViewport,
  Grid as DreiGrid,
} from '@react-three/drei';
import { UseAnimationsReturnType } from '../hooks/useAnimatedMeshViewer';
import { AnimationClip, MeshStandardMaterial, Vector3 } from 'three';
import { noDataContainer } from './MeshViews';

type AnimatedFDAModelProps = {
  setAnimationContext: (context: UseAnimationsReturnType) => void;
  setCurrentFrame: (frame: number) => void;
  animations: AnimationClip[];
  nodes: {
    MeshSkinBinding_0: THREE.SkinnedMesh | null;
    SceneRoot?: THREE.Object3D;
    Armature_0?: THREE.Object3D;
    Meshcapade_Avatar?: THREE.Object3D;
    AnimatedCamera?: THREE.Object3D;
    // You could add specific joint types if needed, but Object3D is sufficient
    [key: string]: THREE.Object3D | undefined | null;
  };
  scene?: THREE.Group; // Add this to access the complete scene if needed
} & JSX.IntrinsicElements['group'];

export const AnimatedFDAModel: FC<AnimatedFDAModelProps> = ({
  setAnimationContext,
  setCurrentFrame,
  animations,
  nodes,
  scene,
  ...props
}) => {
  const group = useRef<any>();
  const useAnimationReturn = useAnimations(animations, group);

  useEffect(() => {
    if (useAnimationReturn.actions) {
      setAnimationContext(useAnimationReturn);

      // Get the first animation and set it to the first frame while paused
      const actionName = useAnimationReturn.names[0];
      const action = useAnimationReturn.actions[actionName];

      if (action) {
        // Reset and configure the animation properly
        action.reset();
        action.time = 0;
        action.enabled = true;
        action.setEffectiveWeight(1);
        action.play();
        action.paused = true;

        // Force an update to apply the pose
        action.getMixer().update(0);
      }
    }
  }, [useAnimationReturn, setAnimationContext]);

  useEffect(() => {
    if (nodes.MeshSkinBinding_0?.material) {
      const material = nodes.MeshSkinBinding_0.material as MeshStandardMaterial;
      // Apply your changes to the material here
      material.color.set('#006ead');
      material.metalness = 0;
      material.roughness = 0;
    }
  }, [nodes.MeshSkinBinding_0?.material]);

  useEffect(() => {
    const objectToTraverse = scene || nodes.SceneRoot || nodes.Armature_0;

    if (objectToTraverse) {
      objectToTraverse.traverse((object) => {
        object.frustumCulled = false;
      });
    }
  }, [scene, nodes]);

  useFrame(() => {
    if (useAnimationReturn.actions) {
      // Get the first animation name from the names array
      const actionName = useAnimationReturn.names[0];
      // Use that name to get the correct animation action
      const currentTime = useAnimationReturn.actions[actionName]?.time || 0;
      const fps = 60;
      const currentFrame = Math.floor(currentTime * fps);
      setCurrentFrame(currentFrame);
    }
  });

  return (
    <group ref={group} {...props} dispose={null}>
      {/* Render the entire scene hierarchy - the most reliable method */}
      {scene && <primitive object={scene} />}

      {/* If you don't have access to scene, try to render the root node */}
      {!scene && nodes.SceneRoot && <primitive object={nodes.SceneRoot} />}

      {/* If no SceneRoot, try Armature */}
      {!scene && !nodes.SceneRoot && nodes.Armature_0 && (
        <primitive object={nodes.Armature_0} />
      )}
    </group>
  );
};

interface FDAMeshViewerProps {
  setAnimationContext: (context: UseAnimationsReturnType) => void;
  setCurrentFrame: (frame: number) => void;
  animations: AnimationClip[] | null;
  nodes: {
    MeshSkinBinding_0: THREE.SkinnedMesh | null;
    [key: string]: THREE.Object3D | undefined | null;
  };
  scene?: THREE.Group;
  hasDataUrl?: boolean;
  cameraFollow?: boolean;
}

// not working as expected - will revist later
const CameraFollower: FC<{
  target: THREE.Object3D | null;
  enabled: boolean;
}> = ({ target, enabled }) => {
  const { camera } = useThree();
  const initialCameraPosition = useRef(new Vector3());
  const initialTargetPosition = useRef(new Vector3());
  const currentTargetPosition = useRef(new Vector3());
  const offsetFromTarget = useRef(new Vector3());

  useEffect(() => {
    if (enabled && target) {
      // Capture the current camera position and target position when follow starts
      initialCameraPosition.current.copy(camera.position);
      target.getWorldPosition(initialTargetPosition.current);

      // Calculate the offset from target to camera at the moment follow is enabled
      offsetFromTarget.current
        .copy(initialCameraPosition.current)
        .sub(initialTargetPosition.current);
    }
  }, [enabled, camera, target]);

  useFrame((state, delta) => {
    if (enabled && target) {
      // Get the target's current world position
      target.getWorldPosition(currentTargetPosition.current);

      // Calculate desired camera position using the preserved offset
      const desiredCameraPosition = currentTargetPosition.current
        .clone()
        .add(offsetFromTarget.current);

      // Smoothly interpolate camera position to follow the target
      const followSpeed = 5;
      camera.position.lerp(desiredCameraPosition, delta * followSpeed);

      // Always look at the target for smooth tracking
      camera.lookAt(currentTargetPosition.current);
    }
  });

  return null;
};

export const FDAMeshViewer: FC<FDAMeshViewerProps> = ({
  setAnimationContext,
  setCurrentFrame,
  animations,
  nodes,
  scene,
  hasDataUrl = true,
  cameraFollow = false,
}) => {
  // Check if essential data is available - prioritize hasDataUrl check
  const hasValidData =
    hasDataUrl &&
    nodes &&
    nodes.MeshSkinBinding_0 &&
    animations &&
    animations.length > 0;

  // If no valid data, show the "data not available" message
  if (!hasValidData) {
    return noDataContainer();
  }

  return (
    <Canvas
      style={{
        height: 'calc(100vh - 180px)',
        minHeight: 400,
        pointerEvents: 'auto',
      }}
      camera={{
        position: [-5, 4, 4],
        near: 0.001,
        far: 1000,
      }}
    >
      <ambientLight intensity={1} color={0xffffff} />
      <directionalLight
        castShadow
        position={[2.5, 5, 5]}
        intensity={1}
        shadow-mapSize={[1024, 1024]}
      >
        <orthographicCamera
          attach='shadow-camera'
          args={[-5, 5, 5, -5, 1, 50]}
        />
      </directionalLight>
      {/* <ambientLight intensity={0.9} color={0xffffff} /> */}
      {/* <spotLight position={[10, 10, 10]} /> */}

      <pointLight position={[-10, -10, -10]} decay={0} intensity={0.5} />
      <AnimatedFDAModel
        setAnimationContext={setAnimationContext}
        setCurrentFrame={setCurrentFrame}
        animations={animations}
        nodes={nodes}
        scene={scene}
      />
      {/* <CameraFollower target={nodes.MeshSkinBinding_0} enabled={cameraFollow} /> */}
      <GizmoHelper alignment='bottom-right' margin={[100, 100]}>
        <GizmoViewport labelColor='white' axisHeadScale={1} />
      </GizmoHelper>
      <OrbitControls makeDefault target={[0, 0, 0]} />
      <DreiGrid
        infiniteGrid
        position={[0, -0.01, 0]}
        cellSize={5}
        cellThickness={1}
        sectionSize={0}
        sectionThickness={0}
        cellColor={'#6f6f6f'}
      />
    </Canvas>
  );
};
